<template>
  <div class="studio-main-layout">
    <!-- Top Bar - Only show in create/edit modes -->
    <div class="studio-top-bar" v-if="isCreating || isEditing">
      <div class="studio-top-bar-content">
        <!-- Left side - Back to list button -->
        <div class="studio-top-left">
          <v-btn class="studio-back-btn" flat @click="handleBackToList" title="Back to List">
            <img :src="back" alt="back" width="16" height="16" />
            <span class="ml-1" style="margin-top: 2px;">Back to List</span>
          </v-btn>
    </div>

        <!-- Center - Title -->
        <div class="studio-top-center">
          <!-- Editable title for create/edit modes -->
          <div v-if="isCreating || isEditing" class="studio-title-input-container">
            <input 
              type="text" 
              v-model="plotName" 
              :style="{ width: inputWidth + 'ch' }" 
              placeholder="untitled"
              class="studio-title-input"
              @input="handlePlotNameChange"
            />
        </div>
          <!-- Static title for view mode -->
          <h3 v-else class="studio-top-title">
            {{ selectedPlot?.Name || 'Plot' }}
          </h3>
                  </div>

        <!-- Right side - Controls -->
        <div class="studio-top-right">
          <div class="studio-top-controls">
            <!-- Access Level Toggle -->
                    <div class="dash-top-right" v-if="isChartCreator">
                      <div class="tab-div dash-access-toggle justify-space-between">
                        <v-btn-toggle v-model="isShared" class="axis-toggle font-overlay" mandatory
                          @update:modelValue="handleAccessToggleChange">
                          <v-btn :value="false" class="switch-btn" outlined>My Plot</v-btn>
                          <v-btn :value="true" class="switch-btn" outlined>Shared</v-btn>
                        </v-btn-toggle>
                      </div>
                    </div>

            <!-- Action Buttons -->
            <template v-if="isCreating || isEditing">
                    <!-- Save Button -->
              <v-btn v-if="isChartCreator" class="studio-edit-btn" flat :disabled="!isFormValid"
                      :loading="loading" @click="handleSavePlot" title="Save Plot">
                      <img :src="saveIcon" alt="save" width="16" height="16" />
                      <span class="ml-1" style="margin-top: 2px;">Save</span>
                    </v-btn>

                    <!-- Delete Button -->
                    <v-btn v-if="isChartCreator" class="studio-delete-btn" flat @click="handleDeletePlot"
                      title="Delete Plot">
                      <img :src="deleteIcon" alt="delete" width="16" height="16" />
                    </v-btn>
          </template>
          <template v-else>
                    <!-- Edit Button -->
                    <v-btn v-if="isChartCreator" class="studio-edit-btn" flat @click="handleEditPlot" 
                           :loading="editLoading" :disabled="editLoading" title="Edit Plot">
                      <img :src="editIcon" alt="edit" width="16" height="16" />
                      Edit
                    </v-btn>
            </template>
          </div>
                  </div>
                </div>
              </div>

    <!-- Main Content Area -->
    <div class="studio-content-wrapper">
      <div class="studio-sidebar">
        <template v-if="isCreating || isEditing">
          <StudioDataLeft ref="studioDataLeft" :selectedChartType="selectedChartType" :isEditing="isEditing"
            :editPlotData="editPlotData" :chartTitle="plotName" @chart-type-changed="handleChartTypeChange" @loading-state="handleLoading"
            @chart-saved="handleChartSaved" @chart-title="handleChartTitle" @x-axis-selected="handleXAxisSelected"
            @y-axis-selected="handleYAxisSelected" @z-axis-selected="handleZAxisSelected"
            @chart-saved-studio="handleChartSavedFromStudio" @update-x-bins="updateXBins" @update-y-bins="updateYBins"
            @grouped-x-axis-data="handleXColorGroupSelected" @grouped-y-axis-data="handleYColorGroupSelected"
            @grouped-z-axis-data="handleZColorGroupSelected" @color-group-key="handleColorGroupKey"
            @color-group-display-name="handleColorGroupDisplayName"
            @sample-error="handleSampleError" @handle-chart-height="handleChartHeight" @x-axis-scale="handleXAxisScale"
            @y-axis-scale="handleYAxisScale" @chart-updated="handleChartUpdated" @edit-cancelled="handleEditCancelled"
            @plot-deleted="handlePlotDeleted" @form-validation="handleFormValidation"
            @update-right-content-style="handleRightContentStyleUpdate" @back-to-list="handleBackToList"
            @access-level-changed="handleAccessLevelChange" @chart-fetched="handleChartFetched" @table-data="handleTableData" />
        </template>
        <template v-else>
          <StudioList ref="studioList" @x-axis-selected="handleXAxisSelected" @y-axis-selected="handleYAxisSelected"
            @z-axis-selected="handleZAxisSelected" @selected-plot="handlePlotSelected" @create-plot="handleCreatePlot" @show-snackbar="handleSnackbar"
            @chart-deleted="handleChartDeleted" @loading-state="handleLoading"
            @grouped-x-axis-data="handleXColorGroupSelected" @grouped-y-axis-data="handleYColorGroupSelected"
            @grouped-z-axis-data="handleZColorGroupSelected" @color-group-key="handleColorGroupKey"
            @color-group-display-name="handleColorGroupDisplayName" @table-data="handleTableData" />
        </template>
      </div>
      
      <div class="studio-content-area" :class="{ 'filter-open': rightDrawerOpen }" :style="studioRightContentStyle">
        <template v-if="!selectedPlot && !isCreating && !isEditing">
          <div class="studio-empty-state">
            <h2>Select or create a plot to get started</h2>
          </div>
        </template>
        <template v-else>
          <div class="studio-flex-row">
            <div class="studio-chart-area" :class="[chartAreaClasses, { 'has-floating-controls': selectedPlot && !isCreating && !isEditing }]" :style="studioRightContentStyle">
              <!-- Floating controls for view-only mode -->
              <div class="studio-floating-controls" v-if="selectedPlot && !isCreating && !isEditing">
                <div class="studio-floating-controls-content">
                  <!-- Access Level Toggle -->
                  <div class="dash-top-right" v-if="isChartCreator">
                    <div class="tab-div dash-access-toggle justify-space-between">
                      <v-btn-toggle v-model="isShared" class="axis-toggle font-overlay" mandatory
                        @update:modelValue="handleAccessToggleChange">
                        <v-btn :value="false" class="switch-btn" outlined>My Plot</v-btn>
                        <v-btn :value="true" class="switch-btn" outlined>Shared</v-btn>
                      </v-btn-toggle>
                    </div>
                  </div>

                  <!-- Edit Button -->
                  <v-btn v-if="isChartCreator" class="studio-edit-btn" flat @click="handleEditPlot" 
                         :loading="editLoading" :disabled="editLoading" title="Edit Plot">
                    <img :src="editIcon" alt="edit" width="16" height="16" />
                    Edit
                  </v-btn>
                </div>
              </div>

              <!-- Show progress bar loader when loading -->
              <div v-if="loading" class="chart-loader">
                <div class="progress-container">
                  <div class="progress-header">
                    <h3>Loading Plot Data</h3>
                    <p>Please wait while we fetch and process your data...</p>
                  </div>
                  <div class="progress-bar-container">
                    <div class="progress-bar">
                      <div class="progress-fill"></div>
                    </div>
                    <div class="progress-text">Processing...</div>
                  </div>
                </div>
              </div>
              <!-- Show chart when not loading -->
              <DynamicChart ref="dynamicChart" v-else v-bind="chartProps" :key="chartRenderKey" />
            </div>
        </div>
      </template>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Dialog -->
  <v-dialog transition="dialog-top-transition" v-model="showDeleteDialog" width="417">
    <div class="cross-btn-div" @click="closeDeleteConfirmation">
      <v-img :src="cross" alt="close"></v-img>
    </div>
    <v-alert title="Delete Plot" :text="confirmDeleteText">
      <div class="alert-btns-div">
        <v-btn class="cancel-btn" flat @click="closeDeleteConfirmation">Cancel</v-btn>
        <v-btn class="delete-btn" flat @click="confirmDelete" :loading="deleteLoading">
          <v-img :src="delete_icon" alt="delete-icon" width="16" height="15" class="mr-2"></v-img>
          Delete Plot</v-btn>
      </div>
    </v-alert>
  </v-dialog>

  <!-- Snackbar for notifications -->
  <v-snackbar v-model="snackbar.visible" :color="snackbar.color" :timeout="snackbar.timeout">
    {{ snackbar.message }}
  </v-snackbar>
</template>

<script>
import StudioList from './StudioList.vue';
import StudioDataLeft from './StudioDataLeft.vue';
import DynamicChart from '../Charts/DynamicChart.vue';
import api from "../../../axiosInterceptor";
import screenWidthMixin from "../../../assets/JS/screenWidthMixin";
import { eventBus } from "../../../eventBus.js";
import { shouldFormatNumbers } from "../../../utils/numberFormatter";

export default {
  name: 'StudioData',
  components: {
    StudioList,
    StudioDataLeft,
    DynamicChart
  },
  mixins: [screenWidthMixin],
  data() {
    return {
      selectedPlot: null,
      isCreating: false,
      isEditing: false,
      editPlotData: null,
      plotName: '',
      isShared: false,
      selectedChartType: "",
      xAxisData: [],
      yAxisData: [],
      zAxisData: [],
      xColorGroupData: {},
      yColorGroupData: {},
      zColorGroupData: {},
      markerSizes: [],
      xAxisDisplayName: "",
      xAxisCaption: "",
      yAxisCaption: "",
      yAxisDisplayNames: [],
      zAxisDisplayName: "",
      loading: false,
      chartName: "",
      xBins: null,
      yBins: null,
      colorGroupKey: "",
      colorGroupDisplayName: "",
      sampleError: "",
      chartHeight: 500,
      xAxisScale: "",
      yAxisScale: "",
      chartRenderKey: 0, // Add this to force chart re-rendering
      back: require("../../../assets/Svg/Dashboard/back.svg"),
      editIcon: require("../../../assets/Svg/Dashboard/pencil.svg"),
      deleteIcon: require("../../../assets/Svg/Dashboard/delete.svg"),
      saveIcon: require("../../../assets/Svg/save.svg"),
      isFormValid: false,
      snackbar: {
        visible: false,
        message: '',
        color: '',
        timeout: 5000
      },
      showDeleteDialog: false,
      deleteLoading: false,
      dashboardUsageCheck: false,
      dashboardUsageList: [],
      dashboardUsageCount: 0,
      confirmDeleteText: "",
      cross: require("../../../assets/Images/Buttons/cross.png"),
      delete_icon: require("../../../assets/Svg/Chart/delete-white.svg"),
      isXLog: false,
      isYLog: false,
      accessLevel: 'private', // Add accessLevel property for create mode
      // AbortController instances for request cancellation
      chartUpdateAbortController: null,
      chartUsageAbortController: null,
      // Raw table data for comprehensive CSV export
      tableData: [],
      chartDeleteAbortController: null,
      // localStorage keys
      localStorageKeys: {
        studioSession: 'fluidsdata_studio_session',
        createMode: 'fluidsdata_studio_create_mode',
        editMode: 'fluidsdata_studio_edit_mode',
        formData: 'fluidsdata_studio_form_data'
      },
      autoSaveInterval: null, // Add autoSaveInterval property
      // Add session state management properties
      lastSavedSession: null,
      saveSessionDebounceTimer: null,
      isSavingSession: false,
      editLoading: false, // Add editLoading property
      editTimeout: null, // Add editTimeout property for cleanup
      // Store the newly created/edited chart to auto-select when going back
      newlyCreatedChart: null,
      shouldAutoSelectNewChart: false
    };
  },
  computed: {
    wrapperClass() {
      const pathSegments = this.$route.path.split('/').filter(Boolean); // Remove empty segments
      const mainRoute = pathSegments.length > 1 ? pathSegments[1].toLowerCase() : null;
      if (mainRoute === 'dashboards') {
        return 'dashboard-wrapper';
      } else if (mainRoute === 'fluidsdata-studio') {
        return 'studio-page-wrapper';
      } else {
        return '';
      }
    },
    inputWidth() {
      const plotName = String(this.plotName || '');
      return plotName.length > 0 ? plotName.length : 15; // Minimum width of 15 characters
    },
    safeChartName() {
      // Ensure chartName is always a string with trim method
      const name = this.chartName;
      if (typeof name === 'string') {
        return name;
      } else if (name && typeof name === 'object' && name.toString) {
        return name.toString();
      } else {
        return String(name || '');
      }
    },
    chartProps() {
      const xAxisColumn = this.getAxisColumnName('x');
      const yAxisColumns = this.getAxisColumnNames('y');
      const zAxisColumn = this.getAxisColumnName('z');
      
      console.log('chartProps: xAxisColumn =', xAxisColumn);
      console.log('chartProps: yAxisColumns =', yAxisColumns);
      console.log('chartProps: zAxisColumn =', zAxisColumn);
      
      const props = {
        chartHeight: this.chartHeight,
        loading: this.loading,
        chartType: this.selectedChartType,
        xAxisData: this.xAxisData,
        yAxisData: this.yAxisData,
        zAxisData: this.zAxisData,
        markerSizes: this.markerSizes,
        xAxisDisplayName: this.xAxisDisplayName,
        xAxisCaption: this.xAxisCaption,
        yAxisCaption: this.yAxisCaption,
        yAxisDisplayNames: this.yAxisDisplayNames,
        zAxisDisplayName: this.zAxisDisplayName,
        chartName: this.chartName,
        xBins: this.xBins,
        yBins: this.yBins,
        xColorGroupData: this.xColorGroupData,
        yColorGroupData: this.yColorGroupData,
        zColorGroupData: this.zColorGroupData,
        colorGroupKey: this.colorGroupKey,
        colorGroupDisplayName: this.colorGroupDisplayName,
        sampleError: this.sampleError,
        xAxisScale: this.xAxisScale,
        yAxisScale: this.yAxisScale,
        // New props for studio screen mode and filter state
        isCreateEditMode: this.isCreating || this.isEditing,
        isFilterOpen: this.rightDrawerOpen,
        // Raw table data for comprehensive CSV export
        tableData: this.tableData,
        // Add missing column properties for dynamic column detection
        xAxisColumn: xAxisColumn,
        yAxisColumns: yAxisColumns,
        zAxisColumn: zAxisColumn,
        // Add decimal precision props - set to 0 for large numbers to show clean k/M/B notation
        xAxisDecimalPrecision: this.getDecimalPrecision('x'),
        yAxisDecimalPrecision: this.getDecimalPrecision('y'),
        zAxisDecimalPrecision: this.getDecimalPrecision('z'),
      };
      
      return props;
    },
    // Check if user can change access level
    canChangeAccessLevel() {
      if (!this.selectedPlot) return true; // Allow for new charts
      const userEmail = this.$store.getters["tenantId/getUser"]?.username;
      return this.selectedPlot.CreatedBy === userEmail;
    },
    // Check if current user is the creator of the chart
    isChartCreator() {
      if (!this.selectedPlot) return true; // Allow for new charts
      const userEmail = this.$store.getters["tenantId/getUser"]?.username;
      return this.selectedPlot.CreatedBy === userEmail;
    },
    currentChartWidth() {
      // Extract width from studioRightContentStyle
      if (this.studioRightContentStyle && this.studioRightContentStyle.width) {
        return this.studioRightContentStyle.width;
      }
      return '90%';
    },
    // Override the rightContentStyle from mixin for studio-specific behavior
    studioRightContentStyle() {
      // For simple selection screen (when no plot is selected), use full width
      if (!this.selectedPlot && !this.isCreating && !this.isEditing) {
        return { width: '70%' };
      }

      // For create/edit screens, use the mixin's studioCreateEditWidth
      if (this.isCreating || this.isEditing) {
        return { width: this.studioCreateEditWidth };
      }

      // For viewing existing plots, use proper width calculation
      if (this.rightDrawerOpen) {
        return { width: 'calc(100% - 310px)' };
      } else {
        return { width: '100%' };
      }
    },
    chartAreaClasses() {
      const classes = {};

      if (this.isCreating || this.isEditing) {
        classes['create-edit'] = true;
        if (this.rightDrawerOpen) {
          classes['filter-open'] = true;
        }
      } else {
        classes['simple-selection'] = true;
        if (this.rightDrawerOpen) {
          classes['filter-open'] = true;
        }
      }

      return classes;
    },
    chartTitleBarClasses() {
      const classes = {};

      if (this.isCreating || this.isEditing) {
        classes['create-edit'] = true;
        if (this.rightDrawerOpen) {
          classes['filter-open'] = true;
        }
      } else {
        classes['simple-selection'] = true;
        if (this.rightDrawerOpen) {
          classes['filter-open'] = true;
        }
      }

      return classes;
    },

    // Session status for debugging
    sessionStatus() {
      return {
        hasSession: this.hasValidSession(),
        isCreating: this.isCreating,
        isEditing: this.isEditing,
        lastSaved: this.getLastSavedTime(),
        sessionAge: this.getSessionAge()
      };
    },

    // Auto-selection status for debugging
    autoSelectionStatus() {
      return {
        shouldAutoSelect: this.shouldAutoSelectNewChart,
        hasNewlyCreatedChart: !!this.newlyCreatedChart,
        newlyCreatedChartId: this.newlyCreatedChart?.ID,
        newlyCreatedChartName: this.newlyCreatedChart?.Name
      };
    }
  },
  watch: {
    selectedPlot: {
      handler(newVal) {
        if (newVal) {
          this.plotName = String(newVal.Name || '');
          this.chartName = String(newVal.Name || '');
          this.isShared = newVal.AccessLevel === 'public';
          this.selectedChartType = newVal.ChartType || '';
          this.xBins = newVal.NumXBins || 0;
          this.yBins = newVal.NumYBins || 0;
          this.xAxisScale = newVal.XAxisScale || 'linear';
          this.yAxisScale = newVal.YAxisScale || 'linear';
          this.isXLog = this.xAxisScale === 'log';
          this.isYLog = this.yAxisScale === 'log';
          
          // Update local storage whenever selectedPlot changes
          localStorage.setItem('savedChartData', JSON.stringify(newVal));
        }
      },
      immediate: true,
    },
    isCreating(newVal) {
      if (newVal) {
        this.plotName = '';
        this.isShared = false;
        this.isEditing = false;
        this.editPlotData = null;
        // Reset all data when creating new plot
        this.resetChartData();
      }
    },
    xAxisData: {
      handler() {
        // Force chart re-render when axis data changes
        this.chartRenderKey++;
      }
    },
    yAxisData: {
      handler() {
        // Force chart re-render when axis data changes
        this.chartRenderKey++;
      }
    },
    zAxisData: {
      handler() {
        // Force chart re-render when axis data changes
        this.chartRenderKey++;
      }
    },
    isEditing(newVal) {
      if (newVal) {
        this.isCreating = false;
      }
    },
    yAxisData: {
      handler() {
      },
      deep: true
    },
    studioRightContentStyle: {
      handler() {
        // Trigger chart resize when width changes
        this.$nextTick(() => {
          this.handleChartResize();
        });
      },
      deep: true
    },
    rightDrawerOpen: {
      handler() {
        // Trigger chart resize when filter drawer state changes
        this.$nextTick(() => {
          this.handleChartResize();
        });
      }
    },
    // Watch for route changes to restore session when returning to studio
    '$route': {
      handler(to, from) {
        // If user is returning to the studio page, try to restore session
        if (to.path.includes('fluidsdata-studio') && from && !from.path.includes('fluidsdata-studio')) {
          this.$nextTick(() => {
            this.restoreSessionState();
          });
        }
      },
      immediate: false
    }
  },
  mounted() {
    // Restore session state when component mounts
    this.restoreSessionState();

    // Start auto-save if in create/edit mode
    if (this.isCreating || this.isEditing) {
      this.startAutoSave();
    }

    // Add page visibility change listener
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Add beforeunload listener to save session when user closes tab/browser
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  beforeDestroy() {
    // Save session state before component is destroyed
    if (this.isCreating || this.isEditing) {
      this.saveSessionState();
    }

    // Stop auto-save
    this.stopAutoSave();

    // Clean up edit operations
    this.cleanupEditOperations();

    // Remove page visibility change listener
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    // Remove beforeunload listener
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    // Cancel all pending requests when component is destroyed
    this.cancelAllRequests();
  },
  beforeUnmount() {
    // Save session state before component is unmounted (Vue 3 compatibility)
    if (this.isCreating || this.isEditing) {
      this.saveSessionState();
    }

    // Stop auto-save
    this.stopAutoSave();

    // Clean up edit operations
    this.cleanupEditOperations();

    // Remove page visibility change listener
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    // Remove beforeunload listener
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    // Cancel all pending requests when component is unmounted (Vue 3 compatibility)
    this.cancelAllRequests();
  },
  methods: {
    // Request cancellation methods
    cancelChartUpdateRequest() {
      if (this.chartUpdateAbortController) {
        this.chartUpdateAbortController.abort();
        this.chartUpdateAbortController = null;
      }
    },

    cancelChartUsageRequest() {
      if (this.chartUsageAbortController) {
        this.chartUsageAbortController.abort();
        this.chartUpdateAbortController = null;
      }
    },

    cancelChartDeleteRequest() {
      if (this.chartDeleteAbortController) {
        this.chartDeleteAbortController.abort();
        this.chartDeleteAbortController = null;
      }
    },

    // Check if axis data should be formatted with k/M/B notation
    shouldFormatAxisData(axisType) {
      if (!this.tableData || this.tableData.length === 0) {
        return false;
      }

      let columnName;
      if (axisType === 'x') {
        // In create mode, use the selected axis key from the left panel
        if (this.isCreating && this.$refs.studioDataLeft?.selectedXAxisKey) {
          columnName = this.$refs.studioDataLeft.selectedXAxisKey;
        } else {
          columnName = this.selectedPlot?.XAxisColumn;
        }
      } else if (axisType === 'y') {
        // In create mode, use the selected axis key from the left panel
        if (this.isCreating && this.$refs.studioDataLeft?.selectedYAxisKey && this.$refs.studioDataLeft.selectedYAxisKey.length > 0) {
          columnName = this.$refs.studioDataLeft.selectedYAxisKey[0];
        } else {
          const yColumns = this.selectedPlot?.YAxisColumn ? this.selectedPlot.YAxisColumn.split(',').map(col => col.trim()).filter(col => col) : [];
          columnName = yColumns.length > 0 ? yColumns[0] : null;
        }
      } else if (axisType === 'z') {
        // In create mode, use the selected axis key from the left panel
        if (this.isCreating && this.$refs.studioDataLeft?.selectedZAxisKey) {
          columnName = this.$refs.studioDataLeft.selectedZAxisKey;
        } else {
          columnName = this.selectedPlot?.ZAxisColumn;
        }
      }

      if (!columnName) return false;

      const values = this.tableData
        .map(item => item[columnName])
        .filter(value => typeof value === 'number' && !isNaN(value));

      return shouldFormatNumbers(values);
    },

    // Get axis column name for create/edit mode
    getAxisColumnName(axisType) {
      if (axisType === 'x') {
        // In create mode, use the selected axis key from the left panel
        if (this.isCreating && this.$refs.studioDataLeft?.selectedXAxisKey) {
          return this.$refs.studioDataLeft.selectedXAxisKey;
        }
        return this.selectedPlot?.XAxisColumn || '';
      } else if (axisType === 'z') {
        // In create mode, use the selected axis key from the left panel
        if (this.isCreating && this.$refs.studioDataLeft?.selectedZAxisKey) {
          return this.$refs.studioDataLeft.selectedZAxisKey;
        }
        return this.selectedPlot?.ZAxisColumn || '';
      }
      return '';
    },

    // Get axis column names for Y axis (can be multiple)
    getAxisColumnNames(axisType) {
      if (axisType === 'y') {
        // In create mode, use the selected axis keys from the left panel
        if (this.isCreating && this.$refs.studioDataLeft?.selectedYAxisKey && this.$refs.studioDataLeft.selectedYAxisKey.length > 0) {
          console.log('getAxisColumnNames(y): Using create mode, selectedYAxisKey =', this.$refs.studioDataLeft.selectedYAxisKey);
          return this.$refs.studioDataLeft.selectedYAxisKey;
        }
        const plotColumns = this.selectedPlot?.YAxisColumn ? this.selectedPlot.YAxisColumn.split(',').map(col => col.trim()).filter(col => col) : [];
        console.log('getAxisColumnNames(y): Using plot mode, YAxisColumn =', plotColumns);
        return plotColumns;
      }
      return [];
    },

    // Get decimal precision for axis formatting
    getDecimalPrecision(axisType) {
      // Use the column names that were just calculated
      let columnName;
      if (axisType === 'x') {
        columnName = this.getAxisColumnName('x');
      } else if (axisType === 'y') {
        const yColumns = this.getAxisColumnNames('y');
        columnName = yColumns && yColumns.length > 0 ? yColumns[0] : null;
      } else if (axisType === 'z') {
        columnName = this.getAxisColumnName('z');
      }

      if (!columnName || !this.tableData || this.tableData.length === 0) {
        return 2; // Default precision
      }

      const values = this.tableData
        .map(item => item[columnName])
        .filter(value => typeof value === 'number' && !isNaN(value));

      const shouldFormat = shouldFormatNumbers(values);
      console.log(`getDecimalPrecision(${axisType}): columnName = ${columnName}, shouldFormat = ${shouldFormat}`);
      
      return shouldFormat ? 0 : 2;
    },

    // Cancel all pending requests
    cancelAllRequests() {
      this.cancelChartUpdateRequest();
      this.cancelChartUsageRequest();
      this.cancelChartDeleteRequest();
    },

    // localStorage session management methods
    saveSessionState() {
      try {
        // Prevent saving if already in progress
        if (this.isSavingSession) {
          return;
        }

        // Create current session data
        const currentSessionData = {
          timestamp: Date.now(),
          isCreating: this.isCreating,
          isEditing: this.isEditing,
          selectedChartType: this.selectedChartType,
          chartName: this.chartName,
          plotName: this.plotName,
          isShared: this.isShared,
          accessLevel: this.accessLevel,
          xAxisScale: this.xAxisScale,
          yAxisScale: this.yAxisScale,
          isXLog: this.isXLog,
          isYLog: this.isYLog,
          xBins: this.xBins,
          yBins: this.yBins,
          colorGroupKey: this.colorGroupKey,
          colorGroupDisplayName: this.colorGroupDisplayName,
          chartHeight: this.chartHeight,
          sampleError: this.sampleError
        };

        // Create current form data
        const currentFormData = {
          xAxisData: this.xAxisData,
          yAxisData: this.yAxisData,
          zAxisData: this.zAxisData,
          xColorGroupData: this.xColorGroupData,
          yColorGroupData: this.yColorGroupData,
          zColorGroupData: this.zColorGroupData,
          markerSizes: this.markerSizes,
          xAxisDisplayName: this.xAxisDisplayName,
          xAxisCaption: this.xAxisCaption,
          yAxisCaption: this.yAxisCaption,
          yAxisDisplayNames: this.yAxisDisplayNames,
          zAxisDisplayName: this.zAxisDisplayName
        };

        // Check if data has actually changed to prevent unnecessary saves
        const sessionChanged = !this.lastSavedSession || 
          JSON.stringify(currentSessionData) !== JSON.stringify(this.lastSavedSession.sessionData) ||
          JSON.stringify(currentFormData) !== JSON.stringify(this.lastSavedSession.formData);

        if (!sessionChanged) {
          return; // No changes, don't save
        }

        // Set saving flag
        this.isSavingSession = true;

        // Save session state
        localStorage.setItem(this.localStorageKeys.studioSession, JSON.stringify(currentSessionData));

        // Save form data separately for better performance
        localStorage.setItem(this.localStorageKeys.formData, JSON.stringify(currentFormData));

        // Save specific mode states
        if (this.isCreating) {
          localStorage.setItem(this.localStorageKeys.createMode, 'true');
        } else if (this.isEditing) {
          localStorage.setItem(this.localStorageKeys.editMode, 'true');
          
          // Update selectedPlot with current form data and save to local storage
          if (this.selectedPlot) {
            this.updateSelectedPlotWithCurrentData();
            localStorage.setItem('savedChartData', JSON.stringify(this.selectedPlot));
          }
        }

        // Update last saved session reference
        this.lastSavedSession = {
          sessionData: currentSessionData,
          formData: currentFormData,
          timestamp: Date.now()
        };

      } catch (error) {
        console.error('Error saving session state:', error);
      } finally {
        // Clear saving flag
        this.isSavingSession = false;
      }
    },

    // Update selectedPlot with current form data during editing
    updateSelectedPlotWithCurrentData() {
      if (!this.selectedPlot) return;

      // Update selectedPlot with current form data
      this.selectedPlot.Name = this.chartName || this.plotName;
      this.selectedPlot.ChartType = this.selectedChartType;
      this.selectedPlot.NumXBins = this.xBins;
      this.selectedPlot.NumYBins = this.yBins;
      this.selectedPlot.XAxisScale = this.xAxisScale;
      this.selectedPlot.YAxisScale = this.yAxisScale;
      this.selectedPlot.AccessLevel = this.isShared ? 'public' : 'private';
      this.selectedPlot.GroupColumn = this.colorGroupKey;
      
      // Update display names
      this.selectedPlot.xAxisDisplayName = this.xAxisDisplayName;
      this.selectedPlot.yAxisDisplayNames = this.yAxisDisplayNames;
      this.selectedPlot.zAxisDisplayName = this.zAxisDisplayName;
      
      // Update axis data
      this.selectedPlot.xAxisData = this.xAxisData;
      this.selectedPlot.yAxisData = this.yAxisData;
      this.selectedPlot.zAxisData = this.zAxisData;
      
      // Update color group data
      this.selectedPlot.xColorGroupData = this.xColorGroupData;
      this.selectedPlot.yColorGroupData = this.yColorGroupData;
      this.selectedPlot.zColorGroupData = this.zColorGroupData;
      
      // Update marker sizes
      this.selectedPlot.markerSizes = this.markerSizes;
    },

    restoreSessionState() {
      try {
        const sessionData = localStorage.getItem(this.localStorageKeys.studioSession);
        const formData = localStorage.getItem(this.localStorageKeys.formData);
        const createMode = localStorage.getItem(this.localStorageKeys.createMode);
        const editMode = localStorage.getItem(this.localStorageKeys.editMode);

        if (sessionData) {
          const session = JSON.parse(sessionData);
          const now = Date.now();
          const sessionAge = now - session.timestamp;

          // Check if session is still valid (24 hours)
          if (sessionAge < 24 * 60 * 60 * 1000) {
            // Restore session state
            this.selectedChartType = session.selectedChartType || "";
            this.chartName = session.chartName || "";
            this.plotName = session.plotName || "";
            this.isShared = session.isShared || false;
            this.accessLevel = session.accessLevel || 'private';
            this.xAxisScale = session.xAxisScale || "";
            this.yAxisScale = session.yAxisScale || "";
            this.isXLog = session.isXLog || false;
            this.isYLog = session.isYLog || false;
            this.xBins = session.xBins || null;
            this.yBins = session.yBins || null;
            this.colorGroupKey = session.colorGroupKey || "";
            this.colorGroupDisplayName = session.colorGroupDisplayName || "";
            this.chartHeight = session.chartHeight || 500;
            this.sampleError = session.sampleError || "";

            // Restore form data if available
            if (formData) {
              const form = JSON.parse(formData);
              this.xAxisData = form.xAxisData || [];
              this.yAxisData = form.yAxisData || [];
              this.zAxisData = form.zAxisData || [];
              this.xColorGroupData = form.xColorGroupData || {};
              this.yColorGroupData = form.yColorGroupData || {};
              this.zColorGroupData = form.zColorGroupData || {};
              this.markerSizes = form.markerSizes || [];
              this.xAxisDisplayName = form.xAxisDisplayName || "";
              this.xAxisCaption = form.xAxisCaption || "";
              this.yAxisCaption = form.yAxisCaption || "";
              this.yAxisDisplayNames = form.yAxisDisplayNames || [];
              this.zAxisDisplayName = form.zAxisDisplayName || "";
            }

            // Restore screen state
            if (createMode === 'true') {
              this.isCreating = true;
              this.isEditing = false;
              this.selectedPlot = null;
            } else if (editMode === 'true') {
              this.isEditing = true;
              this.isCreating = false;
              
              // Restore selectedPlot from savedChartData for edit mode
              const savedChartData = localStorage.getItem('savedChartData');
              if (savedChartData) {
                try {
                  this.selectedPlot = JSON.parse(savedChartData);
                  
                  // Ensure selectedPlot has all necessary properties for chart rendering
                  if (this.selectedPlot) {
                    // Set display names if not already set
                    if (!this.selectedPlot.xAxisDisplayName && this.xAxisDisplayName) {
                      this.selectedPlot.xAxisDisplayName = this.xAxisDisplayName;
                    }
                    if (!this.selectedPlot.yAxisDisplayNames && this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0) {
                      this.selectedPlot.yAxisDisplayNames = [...this.yAxisDisplayNames];
                    }
                    if (!this.selectedPlot.zAxisDisplayName && this.zAxisDisplayName) {
                      this.selectedPlot.zAxisDisplayName = this.zAxisDisplayName;
                    }
                    
                    // Set axis data if not already set
                    if (!this.selectedPlot.xAxisData && this.xAxisData && this.xAxisData.length > 0) {
                      this.selectedPlot.xAxisData = [...this.xAxisData];
                    }
                    if (!this.selectedPlot.yAxisData && this.yAxisData && this.yAxisData.length > 0) {
                      this.selectedPlot.yAxisData = [...this.yAxisData];
                    }
                    if (!this.selectedPlot.zAxisData && this.zAxisData && this.zAxisData.length > 0) {
                      this.selectedPlot.zAxisData = [...this.zAxisData];
                    }
                    
                    // Set color group data if not already set
                    if (!this.selectedPlot.xColorGroupData && this.xColorGroupData) {
                      this.selectedPlot.xColorGroupData = { ...this.xColorGroupData };
                    }
                    if (!this.selectedPlot.yColorGroupData && this.yColorGroupData) {
                      this.selectedPlot.yColorGroupData = { ...this.yColorGroupData };
                    }
                    if (!this.selectedPlot.zColorGroupData && this.zColorGroupData) {
                      this.selectedPlot.zColorGroupData = { ...this.zColorGroupData };
                    }
                    
                    // Set marker sizes if not already set
                    if (!this.selectedPlot.markerSizes && this.markerSizes && this.markerSizes.length > 0) {
                      this.selectedPlot.markerSizes = [...this.markerSizes];
                    }
                  }
                } catch (error) {
                  console.error('Error parsing savedChartData:', error);
                  this.selectedPlot = null;
                }
              } else {
                console.warn('No savedChartData found for edit mode');
                this.selectedPlot = null;
              }
            } else {
              // Check if there's a selected plot in localStorage (from plot menu selection)
              const savedChartData = localStorage.getItem('savedChartData');
              if (savedChartData) {
                try {
                  const plotData = JSON.parse(savedChartData);
                  // Only restore if it's a valid plot with required properties
                  if (plotData && plotData.ID && plotData.ChartType) {
                    this.selectedPlot = plotData;
                    this.isCreating = false;
                    this.isEditing = false;
                    
                    // Initialize plot properties
                    this.selectedChartType = plotData.ChartType;
                    this.chartName = String(plotData.Name || '');
                    this.plotName = String(plotData.Name || '');
                    this.xBins = plotData.NumXBins;
                    this.yBins = plotData.NumYBins;
                    this.colorGroupKey = plotData.GroupColumn;
                    this.xAxisScale = plotData.XAxisScale || 'linear';
                    this.yAxisScale = plotData.YAxisScale || 'linear';
                    this.isXLog = this.xAxisScale === 'log';
                    this.isYLog = this.yAxisScale === 'log';
                    this.xAxisDisplayName = plotData.xAxisDisplayName || plotData.XAxisColumn || "";
                    this.zAxisDisplayName = plotData.zAxisDisplayName || plotData.ZAxisColumn || "";
                    
                    // Initialize Y-axis data
                    if (plotData.yAxisDisplayNames && plotData.yAxisDisplayNames.length > 0) {
                      this.yAxisDisplayNames = [...plotData.yAxisDisplayNames];
                    } else {
                      let yAxisData = plotData.YAxisColumn || plotData.YAxisColumns;
                      if (yAxisData) {
                        if (plotData.processedYAxisDisplayNames) {
                          this.yAxisDisplayNames = plotData.processedYAxisDisplayNames;
                        } else {
                          if (Array.isArray(yAxisData)) {
                            this.yAxisDisplayNames = yAxisData;
                          } else if (typeof yAxisData === 'string' && yAxisData.includes(',')) {
                            this.yAxisDisplayNames = yAxisData.split(',').map(col => col.trim()).filter(col => col);
                          } else {
                            this.yAxisDisplayNames = [yAxisData];
                          }
                        }
                      } else {
                        this.yAxisDisplayNames = [];
                      }
                    }
                    
                    // Initialize color group data
                    if (plotData.GroupColumn) {
                      this.colorGroupKey = plotData.GroupColumn;
                      this.colorGroupDisplayName = plotData.GroupColumnDisplayName || plotData.GroupColumn;
                    }
                  }
                } catch (error) {
                  console.error('Error parsing savedChartData in restoreSessionState:', error);
                  this.selectedPlot = null;
                }
              }
            }

            // Force chart re-render with restored data
            this.$nextTick(() => {
              // Trigger axis selection events to ensure chart data is properly loaded
              if (this.xAxisData && this.xAxisData.length > 0) {
                this.handleXAxisSelected({
                  data: this.xAxisData,
                  displayName: this.xAxisDisplayName,
                  caption: this.xAxisCaption
                });
              }
              
              if (this.yAxisData && this.yAxisData.length > 0) {
                this.handleYAxisSelected({
                  data: this.yAxisData,
                  displayNames: this.yAxisDisplayNames,
                  caption: this.yAxisCaption
                });
              }
              
              if (this.zAxisData && this.zAxisData.length > 0) {
                this.handleZAxisSelected({
                  data: this.zAxisData,
                  displayName: this.zAxisDisplayName,
                  caption: this.zAxisCaption
                });
              }
              
              // Force chart re-render after axis data is loaded
              this.chartRenderKey++;
            });

            return true;
          } else {
            // Session expired, clear localStorage
            this.clearSessionState();
          }
        }
        return false;
      } catch (error) {
        console.error('Error restoring session state:', error);
        this.clearSessionState();
        return false;
      }
    },

    clearSessionState() {
      try {
        localStorage.removeItem(this.localStorageKeys.studioSession);
        localStorage.removeItem(this.localStorageKeys.createMode);
        localStorage.removeItem(this.localStorageKeys.editMode);
        localStorage.removeItem(this.localStorageKeys.formData);
      } catch (error) {
        console.error('Error clearing session state:', error);
      }
    },

    // Auto-save session state periodically
    startAutoSave() {
      // Clear any existing interval
      this.stopAutoSave();
      
      this.autoSaveInterval = setInterval(() => {
        if (this.isCreating || this.isEditing) {
          this.debouncedSaveSession();
        }
      }, 30000); // Save every 30 seconds
    },

    stopAutoSave() {
      if (this.autoSaveInterval) {
        clearInterval(this.autoSaveInterval);
        this.autoSaveInterval = null;
      }
      
      // Clear any pending debounced save
      if (this.saveSessionDebounceTimer) {
        clearTimeout(this.saveSessionDebounceTimer);
        this.saveSessionDebounceTimer = null;
      }
    },

    // Debounced save method to prevent rapid successive saves
    debouncedSaveSession() {
      // Clear any existing timer
      if (this.saveSessionDebounceTimer) {
        clearTimeout(this.saveSessionDebounceTimer);
      }
      
      // Set new timer
      this.saveSessionDebounceTimer = setTimeout(() => {
        this.saveSessionState();
        this.saveSessionDebounceTimer = null;
      }, 1000); // 1 second debounce
    },

    resetChartData() {
      this.xAxisData = [];
      this.yAxisData = [];
      this.zAxisData = [];
      this.xColorGroupData = {};
      this.yColorGroupData = {};
      this.zColorGroupData = {};
      this.markerSizes = [];
      this.xAxisDisplayName = "";
      this.xAxisCaption = "";
      this.yAxisCaption = "";
      this.yAxisDisplayNames = [];
      this.zAxisDisplayName = "";
      this.chartName = "";
      this.xBins = null;
      this.yBins = null;
      this.colorGroupKey = "";
      this.colorGroupDisplayName = "";
      this.sampleError = "";
      this.xAxisScale = "";
      this.yAxisScale = "";
    },
    handlePlotSelected(plot) {
      if (plot) {
        this.selectedPlot = plot;
        this.isCreating = false;
        this.isEditing = false;

        // Update local storage with the selected plot data
        localStorage.setItem('savedChartData', JSON.stringify(plot));

        // Close the filter drawer when transitioning to view mode
        this.handleRightDrawerStateChange(false);

        // Initialize scale values from the selected plot
        this.xAxisScale = plot.XAxisScale || 'linear';
        this.yAxisScale = plot.YAxisScale || 'linear';
        this.isXLog = this.xAxisScale === 'log';
        this.isYLog = this.yAxisScale === 'log';

        // Initialize other plot data
        this.selectedChartType = plot.ChartType;
        this.chartName = String(plot.Name || '');
        this.plotName = String(plot.Name || '');
        this.xBins = plot.NumXBins;
        this.yBins = plot.NumYBins;
        this.colorGroupKey = plot.GroupColumn;
        
        // Set display names - prioritize processed display names over raw column data
        this.xAxisDisplayName = plot.xAxisDisplayName || plot.XAxisColumn || "";
        this.zAxisDisplayName = plot.zAxisDisplayName || plot.ZAxisColumn || "";

        // Initialize Y-axis data from the plot (handle both YAxisColumn and YAxisColumns formats)
        // Prioritize processed display names over raw column data
        if (plot.yAxisDisplayNames && plot.yAxisDisplayNames.length > 0) {
          this.yAxisDisplayNames = [...plot.yAxisDisplayNames];
        } else {
          let yAxisData = plot.YAxisColumn || plot.YAxisColumns;
          if (yAxisData) {
            // Use processed Y-axis data if available (from create/edit flow)
            if (plot.processedYAxisDisplayNames) {
              this.yAxisDisplayNames = plot.processedYAxisDisplayNames;
            } else {
              // Process Y-axis data (same logic as create/edit flow)
              if (Array.isArray(yAxisData)) {
                this.yAxisDisplayNames = yAxisData;
              } else if (typeof yAxisData === 'string' && yAxisData.includes(',')) {
                // Handle comma-separated Y-axis column names (same logic as existing code)
                this.yAxisDisplayNames = yAxisData.split(',').map(col => col.trim()).filter(col => col);
              } else {
                this.yAxisDisplayNames = [yAxisData];
              }
            }
          } else {
            this.yAxisDisplayNames = [];
          }
        }

        // Initialize the access level toggle based on chart's current access level
        this.isShared = plot.AccessLevel === 'public';

        // Initialize color group data if available
        if (plot.GroupColumn) {
          this.colorGroupKey = plot.GroupColumn;
          // Set display name if available, otherwise use the key
          this.colorGroupDisplayName = plot.GroupColumnDisplayName || plot.GroupColumn;
        }

        // Reset chart data and trigger re-render
        // this.resetChartData();
        this.chartRenderKey++;
      }
    },
    handleCreatePlot() {
      localStorage.removeItem("savedChartData")
      localStorage.removeItem("newsavedChartData")
      this.selectedPlot = null;
      this.isCreating = true;
      this.isShared = false; // Default to private for new plots
      this.plotName = ""; // Initialize empty plot name
      this.chartName = ""; // Initialize empty chart name
      this.resetChartData();

      // Close the filter drawer when starting to create a new plot
      this.handleRightDrawerStateChange(false);

      // Start auto-save for create mode
      this.startAutoSave();

      // Save session state
      this.$nextTick(() => {
        this.debouncedSaveSession();
      });
    },
    handleBackToList() {
      // Clear localStorage only if we're not auto-selecting a chart
      if (!this.shouldAutoSelectNewChart || !this.newlyCreatedChart) {
        localStorage.removeItem("savedChartData")
        localStorage.removeItem("newsavedChartData")
      } else {
        // Only clear newsavedChartData, keep savedChartData for auto-selection
        localStorage.removeItem("newsavedChartData")
      }

      // Clear studio session state
      this.clearSessionState();

      // Stop auto-save
      this.stopAutoSave();

      // Close the filter drawer when going back to listing
      this.handleRightDrawerStateChange(false);

      // Check if we should auto-select a newly created/edited chart
      if (this.shouldAutoSelectNewChart && this.newlyCreatedChart) {
        
        try {
          // Ensure Y-axis data is properly processed before auto-selecting
          let yAxisData = this.newlyCreatedChart.YAxisColumn || this.newlyCreatedChart.YAxisColumns;
          if (yAxisData && !this.newlyCreatedChart.processedYAxisDisplayNames) {
            // Process Y-axis data if not already processed
            if (Array.isArray(yAxisData)) {
              this.newlyCreatedChart.processedYAxisDisplayNames = yAxisData;
            } else if (typeof yAxisData === 'string' && yAxisData.includes(',')) {
              // Handle comma-separated Y-axis column names (same logic as existing code)
              this.newlyCreatedChart.processedYAxisDisplayNames = yAxisData.split(',').map(col => col.trim()).filter(col => col);
            } else {
              this.newlyCreatedChart.processedYAxisDisplayNames = [yAxisData];
            }
          }
          
          // Display names are already properly set in handleChartFetched
          // Don't overwrite them with raw column names here
          
                      // Auto-select the newly created/edited chart
            this.handlePlotSelected(this.newlyCreatedChart);
              
            // Ensure ALL required properties are properly synced after auto-selection
            this.$nextTick(() => {
              
              // Sync ALL properties from the selected plot to component properties
              if (this.selectedPlot) {
                // Display names
                if (this.selectedPlot.xAxisDisplayName) {
                  this.xAxisDisplayName = this.selectedPlot.xAxisDisplayName;
                }
                if (this.selectedPlot.yAxisDisplayNames && this.selectedPlot.yAxisDisplayNames.length > 0) {
                  this.yAxisDisplayNames = [...this.selectedPlot.yAxisDisplayNames];
                }
                if (this.selectedPlot.zAxisDisplayName) {
                  this.zAxisDisplayName = this.selectedPlot.zAxisDisplayName;
                }
                
                // Chart properties needed for edit mode
                if (this.selectedPlot.ChartType) {
                  this.selectedChartType = this.selectedPlot.ChartType;
                }
                if (this.selectedPlot.Name) {
                  this.chartName = String(this.selectedPlot.Name);
                }
                if (this.selectedPlot.NumXBins) {
                  this.xBins = this.selectedPlot.NumXBins;
                }
                if (this.selectedPlot.NumYBins) {
                  this.yBins = this.selectedPlot.NumYBins;
                }
                if (this.selectedPlot.GroupColumn) {
                  this.colorGroupKey = this.selectedPlot.GroupColumn;
                }
                if (this.selectedPlot.XAxisScale) {
                  this.xAxisScale = this.selectedPlot.XAxisScale;
                  this.isXLog = this.xAxisScale === 'log';
                }
                if (this.selectedPlot.YAxisScale) {
                  this.yAxisScale = this.selectedPlot.YAxisScale;
                  this.isYLog = this.yAxisScale === 'log';
                }
                if (this.selectedPlot.AccessLevel) {
                  this.isShared = this.selectedPlot.AccessLevel === 'public';
                }
                
                // CRITICAL: Set the actual data arrays that populate the input fields
                if (this.selectedPlot.xAxisData && this.selectedPlot.xAxisData.length > 0) {
                  this.xAxisData = [...this.selectedPlot.xAxisData];
                }
                if (this.selectedPlot.yAxisData && this.selectedPlot.yAxisData.length > 0) {
                  this.yAxisData = [...this.selectedPlot.yAxisData];
                }
                if (this.selectedPlot.zAxisData && this.selectedPlot.zAxisData.length > 0) {
                  this.zAxisData = [...this.selectedPlot.zAxisData];
                }
                
                // Set color group data if available
                if (this.selectedPlot.xColorGroupData) {
                  this.xColorGroupData = { ...this.selectedPlot.xColorGroupData };
                }
                if (this.selectedPlot.yColorGroupData) {
                  this.yColorGroupData = { ...this.selectedPlot.yColorGroupData };
                }
                if (this.selectedPlot.zColorGroupData) {
                  this.zColorGroupData = { ...this.selectedPlot.zColorGroupData };
                }
                
                // Set plot name if available
                if (this.selectedPlot.plotName) {
                  this.plotName = String(this.selectedPlot.plotName);
                }
                
                // Force chart re-render to ensure all data is properly loaded
                this.chartRenderKey++;
              }
            });
            
            // Refresh the chart list to show updated data
            this.refreshChartList();
            
            // Reset the auto-select flag
            this.shouldAutoSelectNewChart = false;
            this.newlyCreatedChart = null;
            
            // Don't reset other state since we're selecting a chart
            return;
        } catch (error) {
          // If auto-selection fails, fall through to normal reset
          this.shouldAutoSelectNewChart = false;
          this.newlyCreatedChart = null;
        }
      }

      // Check if we're going back from edit mode - if so, preserve the selected plot
      const wasEditing = this.isEditing;
      
      // Reset mode flags
      this.isEditing = false;
      this.isCreating = false;
      this.editPlotData = null;
      
      // Only reset selectedPlot if we're not going back from edit mode
      if (!wasEditing) {
        this.selectedPlot = null;
        this.selectedChartType = "";
        this.chartName = "";
        this.plotName = "";
        this.isShared = false;
        this.accessLevel = "private";
        this.isXLog = false;
        this.isYLog = false;

        // Reset chart data
        this.resetChartData();
      } else {
        // When going back from edit mode, keep the selected plot but reset other UI state
        // The selectedPlot watcher will restore the chart properties
        this.isShared = false;
        this.accessLevel = "private";
        this.isXLog = false;
        this.isYLog = false;
        
        // Don't reset chart data - keep the plot selected and loaded
        
        // Force the selectedPlot watcher to trigger by setting it again
        if (this.selectedPlot) {
          // Preserve current display names in the selectedPlot object
          this.selectedPlot.xAxisDisplayName = this.xAxisDisplayName;
          this.selectedPlot.yAxisDisplayNames = this.yAxisDisplayNames;
          this.selectedPlot.zAxisDisplayName = this.zAxisDisplayName;
          
          const plot = this.selectedPlot;
          this.selectedPlot = null;
          this.$nextTick(() => {
            this.selectedPlot = plot;
            
            // Trigger axis selection events to ensure display names are set
            if (this.xAxisData && this.xAxisData.length > 0) {
              this.handleXAxisSelected({
                data: this.xAxisData,
                displayName: this.xAxisDisplayName,
                caption: this.xAxisCaption
              });
            }
            
            if (this.yAxisData && this.yAxisData.length > 0) {
              this.handleYAxisSelected({
                data: this.yAxisData,
                displayNames: this.yAxisDisplayNames,
                caption: this.yAxisCaption
              });
            }
            
            if (this.zAxisData && this.zAxisData.length > 0) {
              this.handleZAxisSelected({
                data: this.zAxisData,
                displayName: this.zAxisDisplayName
              });
            }
            
            // Select the chart in the list when returning from edit mode
            if (this.$refs.studioList && plot.ID) {
              this.$refs.studioList.selectedChartId = plot.ID;
            }
          });
        }
      }

      // Clear any selected chart name
      this.selectedSavedChartName = "";

      // Reset form validation
      this.isFormValid = false;

      // Clear any loading states
      this.loading = false;

      // Reset StudioList component if it exists
      if (this.$refs.studioList) {
        this.$refs.studioList.selectedChartId = null;
      }

      // Refresh the chart list to ensure it's up to date
      this.refreshChartList();

      // Force chart re-render
      this.chartRenderKey++;
    },

    handlePlotNameChange(event) {
      // Get the value from the input event
      const value = event.target.value || '';
      this.plotName = value;
      // Also update chartName for consistency
      this.chartName = value;
      
      // Update selectedPlot.Name if we're in edit mode
      if (this.isEditing && this.selectedPlot) {
        this.selectedPlot.Name = value;
      }
      
      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handlePlotAccessToggleChange() {
      this.isShared = !!this.isShared;
    },

    // Utility method to safely trim chartName
    safeChartNameTrim() {
      const name = this.chartName;
      if (typeof name === 'string') {
        return name.trim();
      } else if (name && typeof name === 'object' && name.toString) {
        return name.toString().trim();
      } else {
        return String(name || '').trim();
      }
    },

    editPlot() { },

    handleChartHeight(inputs) {
      this.chartHeight = inputs;

      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    // Handle scale changes from StudioDataLeft
    handleXAxisScale(scale) {
      this.xAxisScale = scale;
      this.isXLog = scale === 'log';
      this.chartRenderKey++; // Force re-render

      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleYAxisScale(scale) {
      this.yAxisScale = scale;
      this.isYLog = scale === 'log';
      this.chartRenderKey++; // Force re-render

      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    updateXBins(newVal) {
      this.xBins = newVal;
      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    updateYBins(newVal) {
      this.yBins = newVal;

      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleChartTitle(chartTitle) {
      this.chartName = String(chartTitle || '');
      this.plotName = String(chartTitle || '');

      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleLoading(showloaders) {
      this.loading = showloaders;
    },

    handleChartSaved(chartSaved, requestData) {
      this.chartSaved = chartSaved;
      
      // Extract chart ID from requestData
      let chartId = null;
      if (requestData && requestData.data && requestData.data.chart && requestData.data.chart.ID) {
        chartId = requestData.data.chart.ID;
      } else if (requestData && requestData.ID) {
        chartId = requestData.ID;
      }
      
      if (this.isEditing) {
        // Update selectedPlot.Name with the current plot name before exiting edit mode
        if (this.selectedPlot && this.plotName) {
          this.selectedPlot.Name = this.plotName;
        }
        
        // Update localStorage with the edited chart data before auto-selection
        if (this.selectedPlot) {
          localStorage.setItem('savedChartData', JSON.stringify(this.selectedPlot));
        }
        
        // Set up auto-selection for edit mode
        this.shouldAutoSelectNewChart = true;
        this.newlyCreatedChart = { ...this.selectedPlot };
        
        this.isEditing = false;
        this.editPlotData = null;
        // DON'T clear selectedPlot in edit mode - we need it to show the plot view
        // this.selectedPlot = null;
        this.isCreating = false;
        
        // Show success message and redirect to main screen for auto-selection
        this.showSnackbar("Plot updated.", "success");
        
        // Redirect to main screen with auto-selection
        setTimeout(() => {
          this.handleBackToList();
        }, 1000); // 1 second delay to show success message
      } else {
        this.selectedPlot = null;
        this.isCreating = false;
      }

      // Stop auto-save and clear session when chart is saved
      this.stopAutoSave();
      this.clearSessionState();

      // Close the filter drawer when chart is saved
      this.handleRightDrawerStateChange(false);

      // If we have a chart ID, try to select it in the chart list
      if (chartId) {
        // Force refresh when returning from edit mode to ensure bin values are up to date
        this.selectChartById(chartId, this.isEditing);
      }
    },

    handleChartTypeChange(selectedType) {
      this.selectedChartType = selectedType;
    },

    handleXAxisSelected({ data, displayName, caption }) {
      this.xAxisData = data;
      this.xAxisDisplayName = displayName;
      this.xAxisCaption = caption || [];

      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleYAxisSelected({ data, displayNames, caption }) {
      this.yAxisData = data;
      this.yAxisDisplayNames = displayNames || [];
      this.yAxisCaption = caption || [];

      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleZAxisSelected({ data, displayName }) {
      
      this.zAxisData = data;
      this.zAxisDisplayName = displayName;
      this.markerSizes = [...data];

      // Force chart re-render to ensure display name is updated
      this.$nextTick(() => {
        this.chartRenderKey++;
      });

      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleTableData(data) {
      
      this.tableData = data;
    },

    handleXColorGroupSelected(groupedXAxisData) {
      this.xColorGroupData = groupedXAxisData;

      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleYColorGroupSelected(groupedYAxisData) {
      this.yColorGroupData = groupedYAxisData;

      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleZColorGroupSelected(groupedZAxisData) {
      this.zColorGroupData = groupedZAxisData;

      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleColorGroupKey(selectedColorGroupKey) {
      this.colorGroupKey = selectedColorGroupKey;

      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleColorGroupDisplayName(selectedColorGroupDisplayName) {
      this.colorGroupDisplayName = selectedColorGroupDisplayName;

      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleSavedCharts(title) {
      this.$emit("save-chart", title);
      if (this.isEditing) {
        this.isEditing = false;
        this.editPlotData = null;
        this.selectedPlot = null;
        this.isCreating = false;

        // Close the filter drawer when chart is saved
        this.handleRightDrawerStateChange(false);
      }
    },

    // Handle chart saved from studio (create/edit mode)
    handleChartSavedFromStudio(chartData) {
      // Set component properties from chartData so they can be used for edit
      this.selectedChartType = chartData.chartType || this.selectedChartType;
      this.chartName = chartData.chartName || this.chartName;
      this.xBins = chartData.xBins || this.xBins;
      this.yBins = chartData.yBins || this.yBins;
      this.colorGroupKey = chartData.colorGroupKey || this.colorGroupKey;
      this.xAxisScale = chartData.xAxisScale || this.xAxisScale;
      this.yAxisScale = chartData.yAxisScale || this.yAxisScale;
      this.xAxisDisplayName = chartData.xAxisDisplayName || this.xAxisDisplayName;
      this.yAxisDisplayNames = chartData.yAxisDisplayNames || this.yAxisDisplayNames;
      this.zAxisDisplayName = chartData.zAxisDisplayName || this.zAxisDisplayName;
      this.xAxisData = chartData.xAxisData || this.xAxisData;
      this.yAxisData = chartData.yAxisData || this.yAxisData;
      this.zAxisData = chartData.zAxisData || this.zAxisData;
      this.xColorGroupData = chartData.xColorGroupData || this.xColorGroupData;
      this.yColorGroupData = chartData.yColorGroupData || this.yColorGroupData;
      this.zColorGroupData = chartData.zColorGroupData || this.zColorGroupData;
      this.plotName = chartData.plotName || this.plotName;
      this.isShared = typeof chartData.isShared !== 'undefined' ? chartData.isShared : this.isShared;

      // Try to extract display names from the current data if they're not set
      if (!this.xAxisDisplayName && this.xAxisData && this.xAxisData.length > 0) {
        const firstRow = this.xAxisData[0];
        if (firstRow && typeof firstRow === 'object') {
          const keys = Object.keys(firstRow);
          if (keys.length > 0) {
            this.xAxisDisplayName = keys[0];
          }
        }
      }

      if ((!this.yAxisDisplayNames || this.yAxisDisplayNames.length === 0) && this.yAxisData && this.yAxisData.length > 0) {
        const firstRow = this.yAxisData[0];
        if (firstRow && typeof firstRow === 'object') {
          const keys = Object.keys(firstRow);
          if (keys.length > 0) {
            this.yAxisDisplayNames = keys;
          }
        }
      }

      if (!this.zAxisDisplayName && this.zAxisData && this.zAxisData.length > 0) {
        const firstRow = this.zAxisData[0];
        if (firstRow && typeof firstRow === 'object') {
          const keys = Object.keys(firstRow);
          if (keys.length > 0) {
            this.zAxisDisplayName = keys[0];
          }
        }
      }

      // Ensure display names are preserved in the saved chart data
      if (this.xAxisDisplayName) {
        chartData.xAxisDisplayName = this.xAxisDisplayName;
      }
      if (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0) {
        chartData.yAxisDisplayNames = [...this.yAxisDisplayNames];
      }
      if (this.zAxisDisplayName) {
        chartData.zAxisDisplayName = this.zAxisDisplayName;
      }

      // Store the newly created/edited chart data for further use
      this.newlyCreatedChart = chartData;
      this.shouldAutoSelectNewChart = true;
      
      // Extract chart ID from chartData if available
      let chartId = null;
      if (chartData && chartData.ID) {
        chartId = chartData.ID;
      }
      
      // If we're in create mode, we need to wait for the chart to be fully created
      if (this.isCreating) {
        this.shouldAutoSelectNewChart = true;
      }

      // If we're in edit mode, update the selectedPlot with new data
      if (this.isEditing && this.selectedPlot) {
        Object.assign(this.selectedPlot, chartData);
        
        // Update localStorage with the edited chart data
        localStorage.setItem('savedChartData', JSON.stringify(this.selectedPlot));
      }
      
      // If we have a chart ID, try to select it in the chart list
      if (chartId) {
        // Force refresh when returning from edit mode to ensure bin values are up to date
        this.selectChartById(chartId, this.isEditing);
      }
    },

    // Handle chart fetched by ID (complete chart data after creation/update)
    handleChartFetched(completeChartData) {
      
      // Process Y-axis data to handle both YAxisColumn and YAxisColumns formats (same logic as handlePlotSelected)
      let yAxisData = completeChartData.YAxisColumn || completeChartData.YAxisColumns;
      
      if (yAxisData) {
        if (Array.isArray(yAxisData)) {
          completeChartData.processedYAxisDisplayNames = yAxisData;
        } else if (typeof yAxisData === 'string' && yAxisData.includes(',')) {
          // Handle comma-separated Y-axis column names (same logic as existing code)
          completeChartData.processedYAxisDisplayNames = yAxisData.split(',').map(col => col.trim()).filter(col => col);
        } else {
          completeChartData.processedYAxisDisplayNames = [yAxisData];
        }
      } else {
        completeChartData.processedYAxisDisplayNames = [];
      }
      
      // Set display names for proper chart rendering
      // For newly created charts, we'll override these with the user-friendly names from component properties
      if (completeChartData.XAxisColumn) {
        completeChartData.xAxisDisplayName = completeChartData.XAxisColumn;
      }
      if (completeChartData.processedYAxisDisplayNames && completeChartData.processedYAxisDisplayNames.length > 0) {
        completeChartData.yAxisDisplayNames = [...completeChartData.processedYAxisDisplayNames];
      }
      if (completeChartData.ZAxisColumn) {
        completeChartData.zAxisDisplayName = completeChartData.ZAxisColumn;
      }
      
      // Update the newlyCreatedChart with complete data
      if (this.newlyCreatedChart && this.newlyCreatedChart.ID === completeChartData.ID) {
        this.newlyCreatedChart = completeChartData;
      }
      
              // If we're in create mode and this is the chart we just created, update it
        if (this.isCreating && this.shouldAutoSelectNewChart) {
          this.newlyCreatedChart = completeChartData;
          
          // IMPORTANT: Use the original display names from component properties, not raw column names
          // This ensures we show user-friendly names like "Oil Saturation Pressure" instead of "cceOilSaturationPressure"
          if (this.xAxisDisplayName) {
            this.newlyCreatedChart.xAxisDisplayName = this.xAxisDisplayName;
          }
          if (this.yAxisDisplayNames && this.yAxisDisplayNames.length > 0) {
            this.newlyCreatedChart.yAxisDisplayNames = [...this.yAxisDisplayNames];
          }
          if (this.zAxisDisplayName) {
            this.newlyCreatedChart.zAxisDisplayName = this.zAxisDisplayName;
          }
          
          // CRITICAL: Set up editPlotData with the NEW ID and complete data from the created plot
          // This ensures that when user clicks edit, all form fields are populated with the correct data
          // The API response has basic metadata, but we need to combine it with component data for complete editPlotData
          this.editPlotData = {
            // Basic chart properties from API response
            chartType: this.newlyCreatedChart.ChartType || this.selectedChartType,
            chartName: this.newlyCreatedChart.Name || this.chartName,
            xBins: this.newlyCreatedChart.NumXBins || this.xBins,
            yBins: this.newlyCreatedChart.NumYBins || this.yBins,
            colorGroupKey: this.newlyCreatedChart.GroupColumn || this.colorGroupKey,
            xAxisScale: this.newlyCreatedChart.XAxisScale || this.xAxisScale,
            yAxisScale: this.newlyCreatedChart.YAxisScale || this.yAxisScale,
            
            // Display names (use component properties as they have user-friendly names)
            xAxisDisplayName: this.xAxisDisplayName || this.newlyCreatedChart.xAxisDisplayName,
            yAxisDisplayNames: this.yAxisDisplayNames || this.newlyCreatedChart.yAxisDisplayNames,
            zAxisDisplayName: this.zAxisDisplayName || this.newlyCreatedChart.zAxisDisplayName,
            
            // CRITICAL: Use the actual data arrays from component properties (these have the real data)
            xAxisData: this.xAxisData || [],
            yAxisData: this.yAxisData || [],
            zAxisData: this.zAxisData || [],
            
            // Color group data from component properties
            xColorGroupData: this.xColorGroupData || {},
            yColorGroupData: this.yColorGroupData || {},
            zColorGroupData: this.zColorGroupData || {},
            
            // Plot name and access level
            plotName: this.plotName || this.newlyCreatedChart.plotName,
            isShared: this.newlyCreatedChart.AccessLevel === 'public' || this.isShared,
            
            // CRITICAL: Add the raw column names from API response for dropdown population
            // These are needed for Test Type, X-Axis Column, Y-Axis Column dropdowns
            XAxisColumn: this.newlyCreatedChart.XAxisColumn,
            YAxisColumn: this.newlyCreatedChart.YAxisColumn,
            YAxisColumns: this.newlyCreatedChart.YAxisColumns,
            ZAxisColumn: this.newlyCreatedChart.ZAxisColumn,
            TableType: this.newlyCreatedChart.TableType
          };
          
          
          // CRITICAL: Also sync all component properties that handleEditPlot needs
          // This ensures that when edit button is clicked, all properties are available
          if (this.newlyCreatedChart.ChartType) {
            this.selectedChartType = this.newlyCreatedChart.ChartType;
          }
          if (this.newlyCreatedChart.Name) {
            this.chartName = String(this.newlyCreatedChart.Name);
            this.plotName = String(this.newlyCreatedChart.Name);
          }
          if (this.newlyCreatedChart.NumXBins) {
            this.xBins = this.newlyCreatedChart.NumXBins;
          }
          if (this.newlyCreatedChart.NumYBins) {
            this.yBins = this.newlyCreatedChart.NumYBins;
          }
          if (this.newlyCreatedChart.GroupColumn) {
            this.colorGroupKey = this.newlyCreatedChart.GroupColumn;
          }
          if (this.newlyCreatedChart.XAxisScale) {
            this.xAxisScale = this.newlyCreatedChart.XAxisScale;
            this.isXLog = this.xAxisScale === 'log';
          }
          if (this.newlyCreatedChart.YAxisScale) {
            this.yAxisScale = this.newlyCreatedChart.YAxisScale;
            this.isYLog = this.yAxisScale === 'log';
          }
          if (this.newlyCreatedChart.AccessLevel) {
            this.isShared = this.newlyCreatedChart.AccessLevel === 'public';
          }
          if (this.newlyCreatedChart.plotName) {
            this.plotName = String(this.newlyCreatedChart.plotName);
            this.chartName = String(this.newlyCreatedChart.plotName);
          }
          
          
          
          // For create mode, redirect to main screen after chart is fetched
          this.showSnackbar("Plot created successfully!", "success");
          
          // Exit create mode
          this.isCreating = false;
          
          // Stop auto-save for create mode
          this.stopAutoSave();
          
          // Close the filter drawer
          this.handleRightDrawerStateChange(false);
          
          // Redirect to main screen with auto-selection
          setTimeout(() => {
            this.handleBackToList();
          }, 1000); // 1 second delay to show success message
          
        }
      
      // If we're in edit mode and this is the chart we just updated, update it
      if (this.isEditing && this.selectedPlot && this.selectedPlot.ID === completeChartData.ID) {
        // Update the selectedPlot with the complete updated chart data
        this.selectedPlot = completeChartData;
        
        // Also update newlyCreatedChart for consistency
        this.newlyCreatedChart = completeChartData;
        
        // Update local storage with the updated chart data
        localStorage.setItem('savedChartData', JSON.stringify(completeChartData));
        
        // Update component properties with the updated chart data
        this.selectedChartType = completeChartData.ChartType;
        this.chartName = String(completeChartData.Name || '');
        this.plotName = String(completeChartData.Name || '');
        this.xBins = completeChartData.NumXBins;
        this.yBins = completeChartData.NumYBins;
        this.colorGroupKey = completeChartData.GroupColumn;
        this.xAxisScale = completeChartData.XAxisScale || 'linear';
        this.yAxisScale = completeChartData.YAxisScale || 'linear';
        this.isXLog = this.xAxisScale === 'log';
        this.isYLog = this.yAxisScale === 'log';
        this.xAxisDisplayName = completeChartData.xAxisDisplayName || completeChartData.XAxisColumn || "";
        this.zAxisDisplayName = completeChartData.zAxisDisplayName || completeChartData.ZAxisColumn || "";
        
        // Update Y-axis data
        if (completeChartData.yAxisDisplayNames && completeChartData.yAxisDisplayNames.length > 0) {
          this.yAxisDisplayNames = [...completeChartData.yAxisDisplayNames];
        } else {
          let yAxisData = completeChartData.YAxisColumn || completeChartData.YAxisColumns;
          if (yAxisData) {
            if (completeChartData.processedYAxisDisplayNames) {
              this.yAxisDisplayNames = completeChartData.processedYAxisDisplayNames;
            } else {
              if (Array.isArray(yAxisData)) {
                this.yAxisDisplayNames = yAxisData;
              } else if (typeof yAxisData === 'string' && yAxisData.includes(',')) {
                this.yAxisDisplayNames = yAxisData.split(',').map(col => col.trim()).filter(col => col);
              } else {
                this.yAxisDisplayNames = [yAxisData];
              }
            }
          } else {
            this.yAxisDisplayNames = [];
          }
        }
        
        // Update color group data
        if (completeChartData.GroupColumn) {
          this.colorGroupKey = completeChartData.GroupColumn;
          this.colorGroupDisplayName = completeChartData.GroupColumnDisplayName || completeChartData.GroupColumn;
        }
        
        // Update access level
        this.isShared = completeChartData.AccessLevel === 'public';
        
        // Force chart re-render with updated data
        this.$nextTick(() => {
          this.chartRenderKey++;
        });
      }
    },

    handleSampleError(sampleError) {
      this.sampleError = sampleError;

      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    handleFormValidation(isValid) {
      this.isFormValid = isValid;
    },

    handleEditPlot() {
      // Check if user is the creator of the chart
      if (!this.isChartCreator) {
        this.showSnackbar("You can only edit charts that you created.", "error");
        return;
      }

      // Set loading state to prevent multiple clicks
      this.editLoading = true;

      // Add timeout to prevent hanging
      this.editTimeout = setTimeout(() => {
        if (this.editLoading) {
          this.editLoading = false;
          this.showSnackbar("Edit operation timed out. Please try again.", "error");
        }
      }, 10000); // 10 second timeout

      try {
        this.isEditing = true;
        this.isCreating = false;
        
        // Ensure plot name is set from selected plot
        if (this.selectedPlot && this.selectedPlot.Name) {
          this.plotName = String(this.selectedPlot.Name || '');
          this.chartName = String(this.selectedPlot.Name || '');
        }
        
        // Store current plot data for editing
        this.editPlotData = {
          chartType: this.selectedChartType,
          chartName: this.chartName,
          xBins: this.xBins,
          yBins: this.yBins,
          colorGroupKey: this.colorGroupKey,
          xAxisScale: this.xAxisScale,
          yAxisScale: this.yAxisScale,
          xAxisDisplayName: this.xAxisDisplayName,
          yAxisDisplayNames: this.yAxisDisplayNames,
          zAxisDisplayName: this.zAxisDisplayName,
          xAxisData: this.xAxisData,
          yAxisData: this.yAxisData,
          zAxisData: this.zAxisData,
          xColorGroupData: this.xColorGroupData,
          yColorGroupData: this.yColorGroupData,
          zColorGroupData: this.zColorGroupData,
          plotName: this.plotName,
          isShared: this.isShared
        };

        // Close the filter drawer when starting to edit a plot
        this.handleRightDrawerStateChange(false);

        // Start auto-save for edit mode
        this.startAutoSave();

        // Save session state
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });

        // Clear timeout on success
        if (this.editTimeout) {
          clearTimeout(this.editTimeout);
          this.editTimeout = null;
        }

      } catch (error) {
        console.error('Error starting edit mode:', error);
        this.handleEditError(error, 'starting edit mode');
      } finally {
        // Always clear loading state
        this.editLoading = false;
      }
    },

    handleDeletePlot() {
      // Check if user is the creator of the chart
      if (!this.isChartCreator) {
        this.showSnackbar("You can only delete charts that you created.", "error");
        return;
      }

      if (this.selectedPlot && this.selectedPlot.ID) {
        // Check dashboard usage first
        this.checkDashboardUsage();
      }
    },
    handleEditCancelled() {
      this.isEditing = false;
      this.editPlotData = null;

      // Stop auto-save and clear session when edit is cancelled
      this.stopAutoSave();
      this.clearSessionState();

      // Close the filter drawer when edit is cancelled
      this.handleRightDrawerStateChange(false);

      // Restore original plot data
      if (this.selectedPlot) {
        this.handlePlotSelected(this.selectedPlot);
      }
    },

    handlePlotDeleted() {
      this.selectedPlot = null;
      this.isEditing = false;
      this.editPlotData = null;

      // Stop auto-save and clear session when plot is deleted
      this.stopAutoSave();
      this.clearSessionState();

      // Close the filter drawer when plot is deleted
      this.handleRightDrawerStateChange(false);

      this.refreshChartList(); // Call the new method
    },
    async handleSavePlot() {
      // Check if user is the creator of the chart (for edit mode)
      if (this.isEditing && !this.isChartCreator) {
        this.showSnackbar("You can only save charts that you created.", "error");
        return;
      }

      // Trigger save in the StudioDataLeft component
      if (this.$refs.studioDataLeft) {
        try {
          if (this.isEditing) {
            await this.$refs.studioDataLeft.updateChart();
            // For edit mode, just exit edit mode and return to plot view
            this.showSnackbar("Plot updated.", "success");
            
            // Exit edit mode
            this.isEditing = false;
            
            // Stop auto-save for edit mode
            this.stopAutoSave();
            
            // Close the filter drawer
            this.handleRightDrawerStateChange(false);
            
            // IMPORTANT: Preserve the selectedPlot data for display
            // The selectedPlot should already contain the updated chart data
            
            // Update the plot name if it was changed during editing
            if (this.selectedPlot && this.plotName) {
              this.selectedPlot.Name = this.plotName;
            }
            
            // Clear editPlotData after saving
            this.editPlotData = null;
            
            // Force chart re-render to show the updated plot
            this.$nextTick(() => {
              this.chartRenderKey++;
              
              // Ensure the chart is selected in the list when staying in plot view
              if (this.$refs.studioList && this.selectedPlot && this.selectedPlot.ID) {
                this.$refs.studioList.selectedChartId = this.selectedPlot.ID;
              }
            });
            
          } else {
            await this.$refs.studioDataLeft.createChart(this.accessLevel);
            // For create mode, the redirect will happen in handleChartFetched
            // after the chart is fully created and fetched
          }
        } catch (error) {
          console.error('Error saving plot:', error);
          this.showSnackbar("An error occurred while saving the plot.", "error");
        }
      }
    },
    handleChartResize() {
      // Trigger chart re-render when width changes
      this.chartRenderKey++; // Increment key to force re-render
    },
    async handleAccessToggleChange(newAccessLevel) {
      // Check if user is the creator of the chart
      if (!this.isChartCreator) {
        this.showSnackbar("You can only modify charts that you created.", "error");
        return;
      }

      // Ensure access level is "public" or "private" (not true/false)
      let accessLevel = newAccessLevel === true ? "public" : newAccessLevel === false ? "private" : newAccessLevel;
      if (accessLevel !== "public" && accessLevel !== "private") {
        // fallback: default to private if invalid value
        accessLevel = "private";
      }

      // If we're in create mode, just set the accessLevel parameter without making an API call
      if (this.isCreating) {
        // Store the accessLevel in a data property that will be used when saving
        this.accessLevel = accessLevel;
        this.isShared = accessLevel === 'public';

        // Save session state when form data changes
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });

        // Emit the access level change to the parent component (StudioDataLeft)
        //this.handleAccessLevelChange(accessLevel);
        return;
      }

      // For edit mode, proceed with the existing API call logic
      // Find the selected plot from the savedCharts list or from selectedPlot
      const selectedPlot = this.selectedPlot
        ? this.selectedPlot
        : this.savedCharts.find(
          (chart) => chart.Name === this.selectedSavedChartName
        );

      if (!selectedPlot || !selectedPlot.ID) {
        this.showSnackbar("Selected plot not found.", "error");
        return;
      }

      const chartId = selectedPlot.ID;

      // Build the requestData using all parameters from the selected plot, but update AccessLevel
      // Only include fields that should be sent to the API
      const {
        Name,
        ChartType,
        TableType,
        XAxisColumn,
        YAxisColumns,
        ZAxisColumn,
        NumXBins,
        NumYBins,
        GroupColumn,
        Filter,
        XAxisScale,
        YAxisScale,
      } = selectedPlot;

      const requestData = {
        data: {
          chart: {
            name: Name,
            ChartType,
            TableType,
            XAxisColumn,
            YAxisColumns,
            ZAxisColumn,
            NumXBins,
            NumYBins,
            GroupColumn,
            Filter,
            XAxisScale,
            YAxisScale,
            AccessLevel: accessLevel,
          }
        }
      };

      try {
        const response = await api.put(
          `public/api/charts/${chartId}`,
          requestData
        );
        if (
          response &&
          response.data &&
          (response.data.statusCode === 200 || response.data.statusCode === 201)
        ) {
          this.selectedPlot.AccessLevel = accessLevel;
          this.isShared = accessLevel === 'public';
          // Always show success message if statusCode is 200/201
          this.showSnackbar("Plot access level updated successfully.", "success");
          this.chartSaved = true;
          this.$emit("chart-saved", true);
        } else if (response && response.data && response.data.message) {
          this.showSnackbar(response.data.message, "error");
        } else {
          this.showSnackbar("An error occurred while updating the chart.", "error");
        }
      } catch (error) {
        if (error.response && error.response.data && error.response.data.error) {
          this.showSnackbar(error.response.data.error, "error");
        } else if (error.response && error.response.data && error.response.data.message) {
          this.showSnackbar(error.response.data.message, "error");
        } else {
          this.showSnackbar("An error occurred while updating the chart.", "error");
        }
      }
    },
    showSnackbar(message, color = "success") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.visible = true;
    },
    // Update chart access level via API
    async updateChartAccessLevel(newAccessLevel) {
      if (!this.selectedPlot) return;

      // Cancel any previous chart update request
      this.cancelChartUpdateRequest();

      // Create new AbortController for this request
      this.chartUpdateAbortController = new AbortController();

      try {
        const requestData = {
          data: {
            chart: {
              AccessLevel: newAccessLevel
            }
          }
        };

        const response = await api.put(`public/api/charts/${this.selectedPlot.ID}`, requestData, {
          signal: this.chartUpdateAbortController.signal
        });

        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          // Update local selected plot
          this.selectedPlot.AccessLevel = newAccessLevel;

          // Refresh chart list to reflect changes
          this.refreshChartList();

          // Show success message
          this.handleSnackbar({
            message: `Plot access level changed to ${newAccessLevel}`,
            color: "success"
          });
        } else {
          throw new Error(response?.data?.message || "Failed to update plot access level");
        }
      } catch (error) {
        // Check if the request was cancelled
        if (error.name === 'AbortError') {
          return;
        }

        console.error("Error updating chart access level:", error);

        // Don't revert the toggle - keep the user's choice like dashboard
        // this.isShared = this.selectedPlot.AccessLevel === 'public';

        this.handleSnackbar({
          message: error.response?.data?.error || error.message || "An error occurred while updating the plot access level",
          color: "error"
        });
      } finally {
        // Clean up the AbortController
        this.chartUpdateAbortController = null;
      }
    },
    handleRightContentStyleUpdate(newStyle) {
      // Update the mixin's rightDrawerOpen state which will trigger studioRightContentStyle recalculation
      if (newStyle && newStyle.width) {
        // Extract the drawer state from the width calculation
        const isDrawerOpen = newStyle.width.includes('310px');
        this.handleRightDrawerStateChange(isDrawerOpen);
      }
      // Trigger chart resize when filter drawer state changes
      this.$nextTick(() => {
        this.handleChartResize();
      });
    },
    handleSnackbar(messageObj) {
      this.snackbar.message = messageObj.message;
      this.snackbar.color = messageObj.color;
      this.snackbar.timeout = 5000;
      this.snackbar.visible = true;
    },

    // Refresh chart list
    refreshChartList() {
      if (this.$refs.studioList) {
        this.$refs.studioList.getSavedCharts();
      }
    },

    // Handle chart deletion from list
    handleChartDeleted() {
      this.selectedPlot = null;
      this.isEditing = false;
      this.editPlotData = null;

      // Close the filter drawer when chart is deleted from list
      this.handleRightDrawerStateChange(false);

      this.refreshChartList();
    },

    // Handle chart update from list
    handleChartUpdated() {
      // Update selectedPlot.Name with the current plot name if we're in edit mode
      if (this.isEditing && this.selectedPlot && this.plotName) {
        this.selectedPlot.Name = this.plotName;
      }
      this.refreshChartList();
    },

    // Delete Confirmation Dialog Handlers
    async checkDashboardUsage() {
      // Cancel any previous chart usage request
      this.cancelChartUsageRequest();

      // Create new AbortController for this request
      this.chartUsageAbortController = new AbortController();

      try {
        // Make an API call to check if the chart is used in any dashboards
        const response = await api.get(`/public/api/charts/${this.selectedPlot.ID}/usage`, {
          signal: this.chartUsageAbortController.signal
        });
        const data = response.data.data;
        this.dashboardUsageCount = data.length;
        this.dashboardUsageList = data;
        
        // Set the appropriate message based on dashboard usage
        if (data.length === 0) {
          this.confirmDeleteText = `You are deleting the plot ${this.selectedPlot.Name}. Once deleted, it will be permanently removed and cannot be restored. Continue ?`;
        } else {
          this.confirmDeleteText = `The plot ${this.selectedPlot.Name} is currently used in ${data.length} dashboard${data.length > 1 ? 's' : ''}. Deleting it will remove it from all dashboards and permanently delete it from the system. This action cannot be undone. Continue ?`;
        }
        
        this.showDeleteDialog = true;
      } catch (error) {
        // Check if the request was cancelled
        if (error.name === 'AbortError') {
          return;
        }

        console.error('Error checking chart usage:', error);
        // If error, assume no dashboard usage
        this.dashboardUsageCount = 0;
        this.dashboardUsageList = [];
        this.confirmDeleteText = `You are deleting the plot ${this.selectedPlot.Name}. Once deleted, it will be permanently removed and cannot be restored. Continue ?`;
        this.showDeleteDialog = true;
      } finally {
        // Clean up the AbortController
        this.chartUsageAbortController = null;
      }
    },

    async confirmDelete() {
      this.deleteLoading = true;

      // Cancel any previous chart delete request
      this.cancelChartDeleteRequest();

      // Create new AbortController for this request
      this.chartDeleteAbortController = new AbortController();

      try {
        const response = await api.delete(`public/api/charts/${this.selectedPlot.ID}`, {
          signal: this.chartDeleteAbortController.signal
        });
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.handleSnackbar({
            message: `"${this.selectedPlot.Name}" deleted.`,
            color: "success"
          });
          this.selectedPlot = null;
          this.isEditing = false;
          this.editPlotData = null;

          // Close the filter drawer when chart is confirmed deleted
          this.handleRightDrawerStateChange(false);

          this.refreshChartList();
        } else {
          throw new Error(response?.data?.message || "Failed to delete plot");
        }
      } catch (error) {
        // Check if the request was cancelled
        if (error.name === 'AbortError') {
          return;
        }

        console.error("Error deleting plot:", error);
        this.handleSnackbar({
          message: error.response?.data?.error || error.message || "An error occurred while deleting the plot",
          color: "error"
        });
      } finally {
        this.deleteLoading = false;
        this.showDeleteDialog = false;
        // Clean up the AbortController
        this.chartDeleteAbortController = null;
      }
    },
    closeDeleteConfirmation() {
      this.showDeleteDialog = false;
      this.dashboardUsageCount = 0;
      this.dashboardUsageList = [];
    },
    handleXScaleToggleChange(value) {
      this.isXLog = value;
      this.xAxisScale = value ? 'log' : 'linear';
      // Emit the scale change to parent components
      this.$emit('x-axis-scale', this.xAxisScale);
      this.chartRenderKey++; // Force re-render to apply new scale
    },
    handleYScaleToggleChange(value) {
      this.isYLog = value;
      this.yAxisScale = value ? 'log' : 'linear';
      // Emit the scale change to parent components
      this.$emit('y-axis-scale', this.yAxisScale);
      this.chartRenderKey++; // Force re-render to apply new scale
    },
    handleAccessLevelChange(newAccessLevel) {
      this.isShared = newAccessLevel === 'public';
      this.accessLevel = newAccessLevel;
      this.showSnackbar(`Access level changed to ${newAccessLevel}`, 'success');
      this.chartSaved = true;
      this.$emit('chart-saved', true);

      // Save session state when form data changes
      if (this.isCreating || this.isEditing) {
        this.$nextTick(() => {
          this.debouncedSaveSession();
        });
      }
    },

    // Handle page visibility changes (when user switches tabs or minimizes browser)
    handleVisibilityChange() {
      if (document.hidden) {
        // User is hiding the page (e.g., switching tabs, minimizing browser)
        if (this.isCreating || this.isEditing) {
          this.debouncedSaveSession();
        }
      } else {
        // User is showing the page (e.g., returning from minimized browser)
        // No need to save here, as restoreSessionState will handle it
      }
    },

    // Handle beforeunload event to save session when user closes tab/browser
    handleBeforeUnload() {
      if (this.isCreating || this.isEditing) {
        this.debouncedSaveSession();
      }
    },

    // Manual method to clear session state (for debugging or user preference)
    clearSessionManually() {
      this.clearSessionState();
      this.showSnackbar("Session state cleared", "info");
    },

    // Method to manually trigger back to list (useful for testing)
    triggerBackToList() {
      this.handleBackToList();
    },

    // Method to manually sync display names (for debugging)
    syncDisplayNames() {
      
      if (this.selectedPlot) {
        if (this.selectedPlot.xAxisDisplayName) {
          this.xAxisDisplayName = this.selectedPlot.xAxisDisplayName;
        }
        if (this.selectedPlot.yAxisDisplayNames && this.selectedPlot.yAxisDisplayNames.length > 0) {
          this.yAxisDisplayNames = [...this.selectedPlot.yAxisDisplayNames];
        }
        if (this.selectedPlot.zAxisDisplayName) {
          this.zAxisDisplayName = this.selectedPlot.zAxisDisplayName;
        }
        
        
        // Force chart re-render
        this.chartRenderKey++;
      } else {
      }
    },

    // Method to check current display names state (for debugging)
    checkDisplayNamesState() {
      
      if (this.selectedPlot) {
      } else {
      }
      
    },

    // Method to manually set display names from form state (for debugging)
    setDisplayNamesFromForm() {
      
      // Try to extract display names from the current data
      if (this.xAxisData && this.xAxisData.length > 0) {
        const firstRow = this.xAxisData[0];
        if (firstRow && typeof firstRow === 'object') {
          const keys = Object.keys(firstRow);
          if (keys.length > 0) {
            this.xAxisDisplayName = keys[0];
          }
        }
      }
      
      if (this.yAxisData && this.yAxisData.length > 0) {
        const firstRow = this.yAxisData[0];
        if (firstRow && typeof firstRow === 'object') {
          const keys = Object.keys(firstRow);
          if (keys.length > 0) {
            this.yAxisDisplayNames = keys;
          }
        }
      }
      
      if (this.zAxisData && this.zAxisData.length > 0) {
        const firstRow = this.zAxisData[0];
        if (firstRow && typeof firstRow === 'object') {
          const keys = Object.keys(firstRow);
          if (keys.length > 0) {
            this.zAxisDisplayName = keys[0];
          }
        }
      }
      
      
      // Force chart re-render
      this.chartRenderKey++;
    },

    // Method to force display name extraction and save (for debugging)
    forceDisplayNameExtraction() {
      
      // Extract display names from current data
      this.setDisplayNamesFromForm();
      
      // Save session state to preserve the extracted display names
      this.saveSessionState();
      
    },

    // Method to test the complete display names flow (for debugging)
    testDisplayNamesFlow() {
    },

    // Method to test the create -> save -> go back flow (for debugging)
    testCreateSaveFlow() {
      this.checkDisplayNamesState();
      this.forceDisplayNameExtraction();
      
      this.checkDisplayNamesState();
      this.handleBackToList();
    },

    // Method to manually trigger chart save (for debugging)
    triggerChartSave() {
      
      if (this.$refs.studioDataLeft) {
        // Check if we're in create or edit mode
        if (this.isCreating) {
          this.$refs.studioDataLeft.createChart(this.accessLevel);
        } else if (this.isEditing) {
          this.$refs.studioDataLeft.updateChart();
        } else {
        }
      } else {
      }
    },

    // Method to select a chart by ID in the chart list
    selectChartById(chartId, forceRefresh = false) {
      
      // Clear any conflicting localStorage data before selection
      this.clearConflictingLocalStorage();
      
      // Wait for the next tick to ensure StudioList component is available
      this.$nextTick(() => {
        if (this.$refs.studioList) {
          // Call the method on StudioList to select the chart
          this.$refs.studioList.selectChartById(chartId, forceRefresh);
        } else {
          // Retry after a short delay if component is not ready
          setTimeout(() => {
            if (this.$refs.studioList) {
              this.$refs.studioList.selectChartById(chartId, forceRefresh);
            } else {
            }
          }, 500);
        }
      });
    },

    // Method to clear conflicting localStorage data
    clearConflictingLocalStorage() {
      try {
        // Clear old chart data that might cause conflicts
        localStorage.removeItem("newsavedChartData");
        localStorage.removeItem("initialChartData");
        // Keep savedChartData as it will be updated by the selection process
      } catch (error) {
        console.error('Error clearing localStorage:', error);
      }
    },

    // Check if session exists and is valid
    hasValidSession() {
      try {
        const sessionData = localStorage.getItem(this.localStorageKeys.studioSession);
        if (sessionData) {
          const session = JSON.parse(sessionData);
          const now = Date.now();
          const sessionAge = now - session.timestamp;
          return sessionAge < 24 * 60 * 60 * 1000; // 24 hours
        }
        return false;
      } catch (error) {
        return false;
      }
    },

    // Get last saved time for session status
    getLastSavedTime() {
      try {
        const sessionData = localStorage.getItem(this.localStorageKeys.studioSession);
        if (sessionData) {
          const session = JSON.parse(sessionData);
          return new Date(session.timestamp).toLocaleString();
        }
        return null;
      } catch (error) {
        return null;
      }
    },

    // Get session age in human readable format
    getSessionAge() {
      try {
        const sessionData = localStorage.getItem(this.localStorageKeys.studioSession);
        if (sessionData) {
          const session = JSON.parse(sessionData);
          const now = Date.now();
          const sessionAge = now - session.timestamp;
          const hours = Math.floor(sessionAge / (1000 * 60 * 60));
          const minutes = Math.floor((sessionAge % (1000 * 60 * 60)) / (1000 * 60));
          return `${hours}h ${minutes}m ago`;
        }
        return null;
      } catch (error) {
        return null;
      }
    },

    // Clean up edit operations
    cleanupEditOperations() {
      // Clear any pending edit operations
      if (this.editLoading) {
        this.editLoading = false;
      }
      
      // Clear any pending timeouts
      if (this.editTimeout) {
        clearTimeout(this.editTimeout);
        this.editTimeout = null;
      }
      
      // Reset edit state if component is being destroyed while editing
      if (this.isEditing) {
        this.isEditing = false;
        this.editPlotData = null;
      }
      
      // Stop auto-save if it's running
      this.stopAutoSave();
    },

    // Handle errors during edit process
    handleEditError(error, context = 'edit operation') {
      console.error(`Error during ${context}:`, error);
      
      // Reset edit state on error
      this.isEditing = false;
      this.editPlotData = null;
      this.editLoading = false;
      
      // Clear any pending timeout
      if (this.editTimeout) {
        clearTimeout(this.editTimeout);
        this.editTimeout = null;
      }
      
      // Stop auto-save
      this.stopAutoSave();
      
      // Show user-friendly error message
      let errorMessage = "An error occurred during the edit operation. Please try again.";
      
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      this.showSnackbar(errorMessage, "error");
      
      // Emit error event for parent components
      this.$emit('edit-error', { error, context });
    }
  }
};
</script>

<style scoped>
.studio-main-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f5f6fa;
}

.studio-top-bar {
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  height: 52px;
  display: flex;
  align-items: center;
  z-index: 10;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.studio-top-bar-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 6px;
  height: 100%;
}

.studio-top-left {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
}

.studio-top-center {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 0 20px;
}

.studio-top-right {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
}

.studio-top-title {
  font-size: 17.50px;
  font-weight: 600;
  margin: 0;
  color: #222;
  text-align: center;
}

.studio-title-input-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.studio-title-input {
  background: transparent;
  border: none;
  outline: none;
  font-size: 17.50px;
  font-weight: 600;
  color: #222;
  padding: 4px 8px;
  border-radius: 4px;
  border-bottom: 1px dashed #00000066;
  min-width: 15ch;
}


.studio-title-input::placeholder {
  color: #999;
  font-style: italic;
}

.studio-top-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.studio-content-wrapper {
  display: flex;
  height: calc(100vh - 80px);
}



.studio-empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #888;
}
.studio-empty-state h2{
  font-size: 15.5px;
}

/* Removed old title bar styles - now handled by top bar */

.studio-edit-btn {
  background: none;
  border: 1px solid #bdbdbd;
  cursor: pointer;
  height: calc(var(--v-btn-height) - 5px) !important;
  padding: 6px;
  border-radius: 6px;
  gap: 4px;
  font-weight: 500 !important;
  font-style: normal !important;
  font-size: 11.50px;
  line-height: 20px !important;
  text-align: center !important;
  text-transform: none;
  letter-spacing: 0;
}

.studio-delete-btn {
  background: #ffeaea;
  border: 1px solid #e53935;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  gap: 8px;
}

.studio-back-btn,
.studio-save-btn {
  background: none;
  cursor: pointer;
  height: calc(var(--v-btn-height) - 5px) !important;
  padding: 6px;
  border-radius: 4px;
  text-transform: none;
  gap: 1px;
  transition: background-color 0.2s, border-color 0.2s;
}

.studio-edit-btn:hover,
.studio-back-btn:hover,
.studio-save-btn:hover {
  background-color: #f5f5f5;
  border-color: #888;
}

.studio-delete-btn:hover {
  background-color: #ffcccc;
  border-color: #b71c1c;
}

.studio-edit-btn img,
.studio-delete-btn img,
.studio-back-btn img,
.studio-save-btn img {
  opacity: 0.7;
  transition: opacity 0.2s;
}
.studio-back-btn span{
  font-size: 13.50px;
  font-weight: 500;
  line-height: 16px;
  color: #000000CC;
}

.studio-edit-btn:hover img,
.studio-delete-btn:hover img,
.studio-back-btn:hover img,
.studio-save-btn:hover img {
  opacity: 1;
}

.studio-chart-area {
  flex: 1;
  height: 100%; /* Fixed height to prevent overflow */
  overflow: visible; /* Allow content to be visible */
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 0;
  box-sizing: border-box;
}

.studio-chart-area .studio-chart-wrapper .main-chart {
  height: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.studio-chart-area .studio-chart-wrapper .main-chart>div {
  height: 100% !important;
  max-width: 100% !important;
  max-height: 100% !important;
}

/* Shimmer Loader Styles */
.chart-loader {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-container {
  width: 100%;
  max-width: 600px;
  padding: 20px;
  text-align: center;
}

.progress-header h3 {
  margin-bottom: 8px;
  color: #333;
}

.progress-header p {
  margin-bottom: 20px;
  color: #666;
  font-size: 13.50px;
}

.progress-bar-container {
  position: relative;
  width: 100%;
  height: 10px;
  background-color: #e0e0e0;
  border-radius: 5px;
  overflow: hidden;
}

.progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(to right, #2298D5, #2298D5);
  /* A green gradient */
  border-radius: 5px;
  transition: width 0.3s ease-in-out;
  animation: progress-animation 2s ease-in-out infinite;
}

.progress-fill {
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: shimmer 1.5s infinite;
}

.progress-text {
  margin-top: 10px;
  color: #555;
  font-size: 13.50px;
}

@keyframes progress-animation {
  0% {
    width: 0%;
  }

  50% {
    width: 70%;
  }

  100% {
    width: 100%;
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

.studio-flex-row {
  display: flex;
  flex-direction: row;
  height: 100%;
  padding-bottom: 20px;
}

/* Styles for dashboard usage list */
.dashboard-usage-list {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
}

.dashboard-usage-list li {
  margin-bottom: 5px;
  font-size: 13.50px;
  color: #555;
}

.dashboard-usage-list li strong {
  color: #333;
}
</style>